import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePatientFlow } from '../../context/PatientFlowContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  CheckCircle, Building2, MapPin, Star, Clock, 
  ArrowRight, Bell
} from 'lucide-react';
import confetti from 'canvas-confetti';
import api from '../../utils/api';

const ClinicRequestConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { flowState } = usePatientFlow();
  const [selectedClinics, setSelectedClinics] = useState([]);
  const confettiCanvasRef = React.useRef(null);

  useEffect(() => {
    // P7Z-3: This page is now PRESENTATIONAL ONLY. Lead creation is handled
    // by the canonical search → quote-request flow.
    // (POST /api/public/clinics/{id}/request-quote). NO legacy
    // /api/clinics/{id}/leads calls are made here anymore.
    const clinicIds = location.state?.selectedClinics || flowState.clinicsSelected || [];

    if (clinicIds.length === 0) {
      navigate('/search-v2');
      return;
    }

    const fetchClinics = async () => {
      const results = [];
      for (const id of clinicIds) {
        try {
          const res = await api.get(`/public/catalog/clinics/${id}`);
          const data = res.data;
          if (data.found) results.push(data.clinic);
        } catch (e) { console.error('Failed to fetch clinic:', id, e); }
      }
      setSelectedClinics(results);
    };
    fetchClinics();

    // Confetti animation only — no lead writes here.
    const canvas = document.createElement('canvas');
    canvas.style.position = 'fixed';
    canvas.style.top = '0';
    canvas.style.left = '0';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.pointerEvents = 'none';
    canvas.style.zIndex = '9999';
    document.body.appendChild(canvas);
    confettiCanvasRef.current = canvas;
    const myConfetti = confetti.create(canvas, { resize: true });

    const startTimer = setTimeout(() => {
      myConfetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    }, 300);

    const cleanupTimer = setTimeout(() => {
      myConfetti.reset();
      if (confettiCanvasRef.current && confettiCanvasRef.current.parentNode) {
        confettiCanvasRef.current.parentNode.removeChild(confettiCanvasRef.current);
      }
      confettiCanvasRef.current = null;
    }, 4000);

    return () => {
      clearTimeout(startTimer);
      clearTimeout(cleanupTimer);
      myConfetti.reset();
      if (confettiCanvasRef.current && confettiCanvasRef.current.parentNode) {
        confettiCanvasRef.current.parentNode.removeChild(confettiCanvasRef.current);
      }
      confettiCanvasRef.current = null;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Success Header */}
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-12 w-12 text-green-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Request Sent Successfully!
          </h1>
          <p className="text-lg text-gray-600 max-w-xl mx-auto">
            Your quote request has been sent to {selectedClinics.length} clinic{selectedClinics.length !== 1 ? 's' : ''}. 
            They will review your profile and send you personalized offers.
          </p>
        </div>

        {/* What Happens Next */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-blue-600" />
              What Happens Next?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-4 p-4 bg-blue-50 rounded-lg">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                  1
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Clinics Review Your Request</h4>
                  <p className="text-sm text-gray-600">
                    The selected clinics will review your health profile and treatment requirements.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-purple-50 rounded-lg">
                <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                  2
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Receive Personalized Offers</h4>
                  <p className="text-sm text-gray-600">
                    Within 24-48 hours, you'll receive customized treatment offers with pricing, dates, and packages.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-green-50 rounded-lg">
                <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold text-sm shrink-0">
                  3
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900">Compare & Choose</h4>
                  <p className="text-sm text-gray-600">
                    Compare offers side-by-side and choose the best option for you. Then proceed with a deposit to confirm.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Selected Clinics */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-purple-600" />
              Clinics That Will Contact You ({selectedClinics.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {selectedClinics.map(clinic => (
                <div 
                  key={clinic.id}
                  className="flex items-center gap-4 p-4 border rounded-lg hover:bg-gray-50"
                >
                  <img 
                    src={clinic.image} 
                    alt={clinic.name}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900">{clinic.name}</h4>
                    <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                      <MapPin className="h-4 w-4" />
                      <span>{clinic.city}, {clinic.country}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Star className="h-4 w-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-medium">{clinic.rating}</span>
                      <span className="text-xs text-gray-500">({clinic.reviewCount} reviews)</span>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Request Sent
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Notifications Info */}
        <Card className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="py-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                <Bell className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-1">Stay Updated</h4>
                <p className="text-sm text-gray-600">
                  We'll notify you via email and SMS when clinics send you offers. 
                  You can also check your "My Offers" page anytime.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/patient/dashboard')}
          >
            Go to Dashboard
          </Button>
          <Button
            size="lg"
            onClick={() => navigate('/patient/offers')}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            View My Offers
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ClinicRequestConfirmation;
